import { useState, useEffect } from 'react';
import { tutorialsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  BookOpen, 
  Search, 
  LayoutDashboard, 
  Zap, 
  Sparkles, 
  ShieldAlert, 
  Flame, 
  Bell, 
  CreditCard,
  ChevronRight,
  Info,
  Loader2,
  ChevronDown
} from 'lucide-react';

const featureIcons = {
  dashboard: LayoutDashboard,
  analyze: Search,
  'auto-search': Zap,
  'ai-content': Sparkles,
  'bypass-content': ShieldAlert,
  hotface: Flame,
  notifications: Bell,
  billing: CreditCard,
};

const features = [
  { id: 'all', label: 'Tất cả hướng dẫn', icon: BookOpen },
  { id: 'dashboard', label: 'Tổng quan', icon: LayoutDashboard },
  { id: 'analyze', label: 'Phân tích & Lịch sử', icon: Search },
  { id: 'auto-search', label: 'Tự động tìm', icon: Zap },
  { id: 'ai-content', label: 'AI Content', icon: Sparkles },
  { id: 'bypass-content', label: 'Lách Content', icon: ShieldAlert },
  { id: 'hotface', label: 'Hotface List', icon: Flame },
  { id: 'notifications', label: 'Thông báo', icon: Bell },
  { id: 'billing', label: 'Gói VIP', icon: CreditCard },
];

export default function Tutorials() {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeature, setSelectedFeature] = useState('all');

  useEffect(() => {
    const fetchTutorials = async () => {
      try {
        const res = await tutorialsAPI.getAll();
        setTutorials(res.data || []);
      } catch (error) {
        console.error('Failed to fetch tutorials:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTutorials();
  }, []);

  const filteredTutorials = selectedFeature === 'all' 
    ? tutorials 
    : tutorials.filter(t => t.feature_id === selectedFeature);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-[#1877F2] animate-spin" />
      </div>
    );
  }

  const selectedFeatureData = features.find(f => f.id === selectedFeature);

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn px-4 pb-20">
      <div className="bg-white p-5 rounded-3xl border shadow-sm flex flex-col sm:flex-row items-center justify-between gap-6 transition-all hover:shadow-md">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-[#E7F3FF] rounded-2xl text-[#1877F2]">
            {selectedFeatureData ? <selectedFeatureData.icon size={24} /> : <BookOpen size={24} />}
          </div>
          <div>
            <h3 className="font-bold text-lg text-[#1C1E21]">Danh mục hướng dẫn</h3>
            <p className="text-sm text-[#606770]">Chọn tính năng bạn cần hỗ trợ</p>
          </div>
        </div>

        <div className="w-full sm:w-72">
          <Select value={selectedFeature} onValueChange={setSelectedFeature}>
            <SelectTrigger className="h-12 bg-[#F0F2F5] border-none rounded-2xl focus:ring-2 focus:ring-[#1877F2]/20 font-medium">
              <SelectValue placeholder="Chọn danh mục..." />
            </SelectTrigger>
            <SelectContent className="rounded-2xl border-none shadow-xl">
              {features.map(f => (
                <SelectItem key={f.id} value={f.id} className="rounded-xl py-3 focus:bg-[#E7F3FF] focus:text-[#1877F2]">
                  <div className="flex items-center gap-3">
                    <f.icon size={16} />
                    <span>{f.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-6">
        {filteredTutorials.length > 0 ? (
          filteredTutorials.map((tutorial) => {
            const Icon = featureIcons[tutorial.feature_id] || Info;
            const featureLabel = features.find(f => f.id === tutorial.feature_id)?.label || tutorial.feature_id;
            
            return (
              <Card key={tutorial.id} className="overflow-hidden border-none shadow-sm hover:shadow-lg transition-all rounded-3xl group">
                <div className="h-1.5 bg-gradient-to-r from-[#1877F2] to-[#31A24C] opacity-30 group-hover:opacity-100 transition-opacity" />
                <CardHeader className="pb-4 pt-6 px-8">
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2.5 bg-[#F0F2F5] rounded-2xl text-[#1877F2] group-hover:bg-[#1877F2] group-hover:text-white transition-colors">
                        <Icon size={20} />
                      </div>
                      <Badge variant="secondary" className="bg-[#E7F3FF] text-[#1877F2] border-none px-3 py-1 rounded-full text-xs font-semibold">
                        {featureLabel}
                      </Badge>
                    </div>
                    <span className="text-[10px] uppercase font-bold text-[#8C939D] tracking-widest">{new Date(tutorial.updated_at).toLocaleDateString('vi-VN')}</span>
                  </div>
                  <CardTitle className="text-2xl font-black text-[#1C1E21] leading-tight">{tutorial.title}</CardTitle>
                </CardHeader>
                <CardContent className="px-8 pb-8">
                  <div 
                    className="prose prose-sm max-w-none text-[#1C1E21] leading-relaxed whitespace-pre-wrap text-base"
                    dangerouslySetInnerHTML={{ __html: tutorial.content }}
                  />
                </CardContent>
              </Card>
            );
          })
        ) : (
          <div className="bg-white rounded-[2rem] p-16 text-center border shadow-sm border-dashed">
            <div className="w-24 h-24 bg-[#F0F2F5] rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse">
              <BookOpen size={48} className="text-[#8C939D] opacity-20" />
            </div>
            <h3 className="text-2xl font-black text-[#1C1E21] mb-3">Chưa có hướng dẫn cho mục này</h3>
            <p className="text-[#606770] max-w-md mx-auto">
              Đội ngũ đang nỗ lực hoàn thiện nội dung. Bạn có thể xem các danh mục khác hoặc quay lại sau nhé!
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
